<template>
  <div class="p-4" dir="rtl">
    <h1 class="text-2xl font-bold mb-1">الجلسات البديلة (إقرأ)</h1>
    <p class="text-sm text-gray-500 mb-4">
      متابعة الجلسات التي اعتذر عنها الطالب أو أتاحها المعلم: صاحب الجلسة الأساسية، من دخل مكانه،
      متى أُتيحت، ومتى دخل البديل. ويمكن إلغاء اعتذار الطالب طالما لم يدخل بديل بعد.
    </p>

    <div class="flex flex-wrap gap-3 mb-4 items-end bg-gray-50 p-3 rounded border">
      <div>
        <label class="block mb-1 text-sm">الحالة</label>
        <select v-model="filters.status" class="border px-3 py-2 rounded w-48">
          <option value="">الكل</option>
          <option value="available">مُتاحة بانتظار بديل</option>
          <option value="replaced">تم الاستبدال</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">من تاريخ</label>
        <input v-model="filters.dateFrom" type="date" class="border px-3 py-2 rounded" />
      </div>
      <div>
        <label class="block mb-1 text-sm">إلى تاريخ</label>
        <input v-model="filters.dateTo" type="date" class="border px-3 py-2 rounded" />
      </div>
      <div class="flex-1 min-w-[200px]">
        <label class="block mb-1 text-sm">بحث (الطالب / البديل / المعلم / الجدول)</label>
        <input v-model="filters.search" type="text" class="border px-3 py-2 rounded w-full" placeholder="نص للبحث" />
      </div>
      <button @click="search" class="bg-blue-600 text-white px-4 py-2 rounded">بحث</button>
      <button @click="resetFilters" class="bg-gray-200 px-4 py-2 rounded">إزالة الفلاتر</button>
      <button @click="load" class="bg-white border px-4 py-2 rounded" title="تحديث">↻</button>
    </div>

    <div v-if="message" :class="messageClass" class="mb-3 px-4 py-2 rounded border text-sm">{{ message }}</div>

    <div v-if="loading" class="py-6 text-center text-gray-500">جاري التحميل...</div>
    <div v-else>
      <div v-if="rows.length === 0" class="py-6 text-gray-500 text-center">لا توجد جلسات بديلة مطابقة</div>

      <div v-else class="overflow-x-auto">
        <table class="w-full border text-sm bg-white">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="p-2 border whitespace-nowrap">تاريخ الجلسة</th>
              <th class="p-2 border">الجدول / الفترة</th>
              <th class="p-2 border">المعلم</th>
              <th class="p-2 border">صاحب الجلسة الأساسية</th>
              <th class="p-2 border">البديل (من دخل مكانه)</th>
              <th class="p-2 border whitespace-nowrap">متى أُتيحت / السبب</th>
              <th class="p-2 border whitespace-nowrap">متى دخل البديل</th>
              <th class="p-2 border">الحالة</th>
              <th class="p-2 border">إجراء</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in rows" :key="item.enrollmentId" :class="item.status === 'available' ? 'bg-amber-50' : ''">
              <td class="p-2 border whitespace-nowrap text-xs">
                <div>{{ formatDay(item.sessionDate) }}</div>
                <div class="text-gray-500">{{ weekdayLabel(item.weekday) }}</div>
              </td>
              <td class="p-2 border text-xs">
                <div class="text-gray-700">{{ item.scheduleTitle || '—' }}</div>
                <div class="text-gray-500">{{ item.slotName || '—' }}</div>
              </td>
              <td class="p-2 border text-xs">{{ item.teacherName || '—' }}</td>
              <td class="p-2 border text-xs">
                <div>{{ item.originalStudentName || '—' }}</div>
                <div class="text-gray-500">{{ item.originalStudentId ? '#' + item.originalStudentId : '' }}</div>
              </td>
              <td class="p-2 border text-xs">
                <template v-if="item.substituteStudentName">
                  <div>{{ item.substituteStudentName }}</div>
                  <div class="text-gray-500">{{ item.substituteStudentId ? '#' + item.substituteStudentId : '' }}</div>
                </template>
                <span v-else class="text-gray-400">— لا يوجد بعد —</span>
              </td>
              <td class="p-2 border text-xs whitespace-nowrap">
                <div>{{ formatDateTime(item.madeAvailableAt) || formatDay(item.sessionDate) }}</div>
                <div>
                  <span :class="sourceBadgeClass(item.madeAvailableSource)">{{ sourceLabel(item.madeAvailableSource) }}</span>
                </div>
                <div v-if="item.madeAvailableByName" class="text-gray-500">بواسطة: {{ item.madeAvailableByName }}</div>
                <div v-if="!item.hasSnapshot" class="text-gray-400" title="سجل قديم بدون لقطة استرجاع كاملة">الوقت تقريبي</div>
              </td>
              <td class="p-2 border text-xs whitespace-nowrap">
                <template v-if="item.substituteJoinedAt">
                  <div>{{ formatDateTime(item.substituteJoinedAt) }}</div>
                  <div class="text-gray-500">دخول فعلي للغرفة</div>
                </template>
                <template v-else-if="item.substituteClaimedAt">
                  <div>{{ formatDateTime(item.substituteClaimedAt) }}</div>
                  <div class="text-gray-500">وقت الحجز</div>
                </template>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="p-2 border whitespace-nowrap">
                <span :class="statusBadgeClass(item.status)">{{ statusLabel(item.status) }}</span>
              </td>
              <td class="p-2 border whitespace-nowrap">
                <button
                  v-if="item.canRestore"
                  :disabled="restoringId === item.enrollmentId"
                  @click="restore(item)"
                  class="bg-red-600 text-white px-3 py-1 rounded text-xs disabled:opacity-50">
                  {{ restoringId === item.enrollmentId ? '...' : 'إلغاء الاعتذار' }}
                </button>
                <span v-else class="text-gray-400 text-xs" title="لا يمكن الإلغاء بعد دخول بديل">غير متاح</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between mt-4">
        <div class="text-sm text-gray-600">
          صفحة {{ currentPage + 1 }} من {{ totalPages || 1 }} — إجمالي {{ totalElements }}
        </div>
        <div class="flex items-center gap-2">
          <button :disabled="currentPage === 0" @click="prev" class="px-3 py-1 border rounded disabled:opacity-50">السابق</button>
          <button :disabled="currentPage >= totalPages - 1" @click="next" class="px-3 py-1 border rounded disabled:opacity-50">التالي</button>
          <select v-model.number="pageSize" @change="reload" class="border px-2 py-1 rounded">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          <select v-model="direction" @change="reload" class="border px-2 py-1 rounded">
            <option value="desc">الأحدث أولاً</option>
            <option value="asc">الأقدم أولاً</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'IqraSubstituteSessions',
  data() {
    return {
      rows: [],
      loading: false,
      restoringId: null,
      message: '',
      messageType: 'info',
      currentPage: 0,
      pageSize: 20,
      totalPages: 0,
      totalElements: 0,
      direction: 'desc',
      filters: {
        status: '',
        dateFrom: '',
        dateTo: '',
        search: '',
      },
    };
  },
  computed: {
    messageClass() {
      if (this.messageType === 'error') return 'bg-red-50 border-red-200 text-red-700';
      if (this.messageType === 'success') return 'bg-green-50 border-green-200 text-green-700';
      return 'bg-blue-50 border-blue-200 text-blue-700';
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    buildParams() {
      const f = this.filters;
      return {
        page: this.currentPage,
        size: this.pageSize,
        direction: this.direction,
        status: f.status || undefined,
        dateFrom: f.dateFrom || undefined,
        dateTo: f.dateTo || undefined,
        search: f.search?.trim() || undefined,
      };
    },
    async load() {
      this.loading = true;
      try {
        const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.iqra_substitute_sessions, {
          params: this.buildParams(),
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        const data = res.data?.result?.data || res.data?.data;
        this.rows = data?.content || [];
        this.totalElements = data?.totalElements || 0;
        this.totalPages = data?.totalPages || 0;
        this.currentPage = data?.currentPage || 0;
        this.pageSize = data?.pageSize || this.pageSize;
      } catch (e) {
        console.error('Failed loading substitute sessions', e);
        this.rows = [];
        this.setMessage('تعذر تحميل البيانات', 'error');
      } finally {
        this.loading = false;
      }
    },
    async restore(item) {
      const ok = window.confirm(
        `إلغاء اعتذار الطالب "${item.originalStudentName || ''}" واسترجاع جلسته؟\nلن يكون متاحًا لطلاب آخرين بعد الإلغاء.`
      );
      if (!ok) return;
      this.restoringId = item.enrollmentId;
      try {
        const base = process.env.VUE_APP_SERVICE_URL + url.iqra_substitute_sessions;
        const res = await axios.put(
          `${base}/${item.enrollmentId}/restore`,
          {},
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        const msg = res.data?.result?.mesAr || 'تم إلغاء الاعتذار بنجاح';
        this.setMessage(msg, 'success');
        this.load();
      } catch (e) {
        const msg = e?.response?.data?.result?.mesAr || 'فشل في إلغاء الاعتذار';
        this.setMessage(msg, 'error');
      } finally {
        this.restoringId = null;
      }
    },
    setMessage(text, type) {
      this.message = text;
      this.messageType = type || 'info';
      setTimeout(() => { this.message = ''; }, 6000);
    },
    search() { this.currentPage = 0; this.load(); },
    resetFilters() {
      this.filters = { status: '', dateFrom: '', dateTo: '', search: '' };
      this.currentPage = 0;
      this.load();
    },
    prev() { if (this.currentPage > 0) { this.currentPage--; this.load(); } },
    next() { if (this.currentPage < this.totalPages - 1) { this.currentPage++; this.load(); } },
    reload() { this.currentPage = 0; this.load(); },
    formatDay(d) {
      if (!d) return '—';
      try { return new Date(d).toLocaleDateString('ar-SA'); } catch { return d; }
    },
    formatDateTime(iso) {
      if (!iso) return '';
      try { return new Date(iso).toLocaleString('ar-SA'); } catch { return iso; }
    },
    weekdayLabel(w) {
      // Iqra mapping: 1..6 => Mon..Sat, 7 => Sun
      return {
        1: 'الاثنين', 2: 'الثلاثاء', 3: 'الأربعاء', 4: 'الخميس',
        5: 'الجمعة', 6: 'السبت', 7: 'الأحد',
      }[w] || '';
    },
    statusLabel(s) {
      return { available: 'مُتاحة بانتظار بديل', replaced: 'تم الاستبدال' }[s] || s;
    },
    statusBadgeClass(s) {
      const base = 'px-2 py-1 rounded text-white text-xs whitespace-nowrap';
      if (s === 'replaced') return base + ' bg-green-600';
      if (s === 'available') return base + ' bg-amber-500';
      return base + ' bg-gray-600';
    },
    sourceLabel(s) {
      return { student_apology: 'اعتذار الطالب', teacher_release: 'إتاحة المعلم' }[s] || '—';
    },
    sourceBadgeClass(s) {
      const base = 'inline-block px-2 py-0.5 rounded text-xs';
      if (s === 'student_apology') return base + ' bg-orange-100 text-orange-700';
      if (s === 'teacher_release') return base + ' bg-indigo-100 text-indigo-700';
      return base + ' bg-gray-100 text-gray-500';
    },
  },
};
</script>
